import React, { useState, useEffect } from 'react';
import { Card, CardBody, Col, CardHeader, Input, Row } from 'reactstrap';
import Chart from 'react-google-charts';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux-toolkit/store';
import { API } from '../../../api/API';
import { getData, postData } from '../../../CommonElements/Functions/PostGetFun';
import { getFilteredPositionIds, getDataFromDexie } from '../../../db/DexieServises';

interface ClassificationOption {
  id: string;
  name: string;
}

const ClassificationChart = () => {
  const positions = useSelector((state: RootState) => state.position.positions);
  const unitId = useSelector((state: RootState) => state.orgUnit.id);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedClassification, setSelectedClassification] = useState('all');
  const [classificationOptions, setClassificationOptions] = useState<ClassificationOption[]>([
    { id: 'all', name: 'All Classifications' }
  ]);

  // Fetch classification options from Dexie filtered by orgunitid
  const fetchClassificationOptions = async () => {
    try {
      const allClassifications = await getDataFromDexie('Classification');
      
      if (!allClassifications || allClassifications.length === 0) {
        console.warn('⚠️ No classifications found in Dexie');
        setClassificationOptions([{ id: 'all', name: 'All Classifications' }]);
        return;
      }
      
      // Filter by Orgunitid - include items where Orgunitid matches unitId or is null
      const filteredClassifications = allClassifications.filter(
        (item: any) => item.Orgunitid === unitId || item.Orgunitid === null || item.Orgunitid === undefined
      );
      console.log(allClassifications,'============',unitId,'======---------------',filteredClassifications);

      const options: ClassificationOption[] = [
        { id: 'all', name: 'All Classifications' },
        ...filteredClassifications.map((item: any) => ({
          id: String(item.Id),
          name: item.Classification
        }))
      ];

      setClassificationOptions(options);
    } catch (error) {
      console.error('Error fetching classification options:', error);
      setClassificationOptions([{ id: 'all', name: 'All Classifications' }]);
    }
  };

  useEffect(() => {
    fetchClassificationOptions();
  }, [unitId]);

  // Generate month options for last 12 months
  const generateMonthOptions = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleString('default', { month: 'short', year: 'numeric' });
      months.push({ value, label });
    }
    return months;
  };

  const monthOptions = generateMonthOptions();

  const fetchChartData = async () => {
    setLoading(true);
    try {
      const serverUrl = localStorage.getItem('serverUrl');
      const url = `${serverUrl}${API.classificationChart}`;
      
      let positionIds = '';
      if (positions.length > 0) {
        positionIds = positions.map((pos) => pos.id).join(',');
      } else if (unitId && String(unitId) !== '0') {
        positionIds = (await getFilteredPositionIds('Position', unitId)).join(',');
      }
      
      if (!positionIds) {
        console.warn('⚠️ No position IDs available');
        setChartData([]);
        setLoading(false);
        return;
      }

      const params: Record<string, string> = {
        position_id: positionIds.toString(),
        moye: selectedMonth,
      };
      
      // Add classification_id only if not 'all'
      if (selectedClassification !== 'all') {
        params.classification_id = selectedClassification;
      }
      console.log(positionIds,'==========positionids',selectedMonth,'===========selectedMonth',selectedClassification,'====selectedClassification');

      const { status, data }: { status: number; data: { data: Record<string, Record<string, number>> } } = 
        await postData(url, true, params);

      if (status === 200 && data?.data) {
        const transformedData = transformDataForChart(data.data);
        setChartData(transformedData);
      } else {
        setChartData([]);
      }
    } catch (error) {
      console.error('Error fetching classification chart data:', error);
      setChartData([]);
    }
    setLoading(false);
  };

  const transformDataForChart = (apiData: Record<string, Record<string, number>>) => {
    // Get all unique inputs across all classifications
    const allInputs = new Set<string>();
    const classifications = Object.keys(apiData);

    classifications.forEach((classification) => {
      Object.keys(apiData[classification]).forEach((input) => {
        allInputs.add(input);
      });
    });

    const inputsArray = Array.from(allInputs);

    if (inputsArray.length === 0 || classifications.length === 0) {
      return [];
    }

    // Build header row: ['Input', 'Classification1', 'Classification2', ...]
    const header = ['Input', ...classifications];

    // Build data rows: ['InputName', value1, value2, ...]
    const rows = inputsArray.map((input) => {
      const row: (string | number)[] = [input];
      classifications.forEach((classification) => {
        row.push(apiData[classification][input] || 0);
      });
      return row;
    });

    return [header, ...rows];
  };

  useEffect(() => {
    fetchChartData();
  }, [selectedMonth, selectedClassification, positions, unitId]);

  const options = {
    title: 'Classification Chart',
    hAxis: { title: 'Inputs', slantedText: true, slantedTextAngle: 45 },
    vAxis: { title: 'Value', minValue: 0 },
    legend: { position: 'top' },
    bar: { groupWidth: '75%' },
    colors: ['#4CAF50', '#2196F3', '#FF9800', '#E91E63', '#9C27B0', '#00BCD4'],
    isStacked: false,
  };

  return (
    <Col className="col-sm-12 col-xl-12 box-col-12">
      <Card>
        <CardHeader className="pb-0">
          <Row className="align-items-center">
            <Col md={4}>
              <h5>Input Classification</h5>
            </Col>
            <Col md={4}>
              <Input
                type="select"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                bsSize="sm"
              >
                {monthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={4}>
              <Input
                type="select"
                value={selectedClassification}
                onChange={(e) => setSelectedClassification(e.target.value)}
                bsSize="sm"
              >
                {classificationOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <span className="spinner-border text-primary" role="status" aria-hidden="true" />
            </div>
          ) : chartData.length > 1 ? (
            <Chart
              width={'100%'}
              height={'400px'}
              chartType="ColumnChart"
              loader={<div>Loading Chart...</div>}
              data={chartData}
              options={options}
            />
          ) : (
            <div className="text-center text-muted py-5">
              No data available for the selected filters
            </div>
          )}
        </CardBody>
      </Card>
    </Col>
  );
};

export default ClassificationChart;